import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, MapPin, ArrowRight, Wallet, CreditCard, Star, BellRing, BellOff, Mail, MailX, Search, Smartphone, MessageSquareOff, Loader2 } from "lucide-react";
import { VEHICLE_LABEL } from "../lib/pricing";
import TransporterProfileCard from "../components/TransporterProfileCard";
import StripeConnectCard from "../components/StripeConnectCard";
import TransporterAnalytics from "../components/TransporterAnalytics";
import BillingInfoCard from "../components/BillingInfoCard";
import PushNotificationsCard from "../components/PushNotificationsCard";
import DriverRegistry from "../components/DriverRegistry";
import { BackButton } from "../components/BackButton";

const PAYMENT_LABEL = {
  unpaid: { label: "À PAYER", color: "bg-amber-500 text-white" },
  pending: { label: "EN COURS", color: "bg-amber-400 text-white" },
  paid: { label: "PAYÉE", color: "bg-blue-600 text-white" },
  payout_pending: { label: "VERSEMENT EN COURS", color: "bg-amber-500 text-white" },
  released: { label: "REVERSÉE", color: "bg-emerald-600 text-white" },
  refund_pending: { label: "REMBOURSEMENT EN COURS", color: "bg-amber-500 text-white" },
  partial_refunded: { label: "REMBOURSEMENT PARTIEL", color: "bg-orange-500 text-white" },
  partially_refunded: { label: "REMBOURSEMENT PARTIEL", color: "bg-orange-500 text-white" },
  refunded: { label: "REMBOURSÉE", color: "bg-red-600 text-white" },
};

export default function TransporterDashboard() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [missionsData, setMissionsData] = useState({ missions: [], my_missions: [], kyc_status: "incomplete", blocked: true });
  const [earnings, setEarnings] = useState({ total: 0, pending_payout: 0, missions_count: 0 });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const FILTERS_KEY = "mml_transporter_filters_v1";
  const DEFAULT_FILTERS = { keyword: "", city: "", vehicleType: "", priceMin: "", priceMax: "", maxDistance: "", minShipperRating: "", sort: "distance" };
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || "null");
      return saved ? { ...DEFAULT_FILTERS, ...saved } : DEFAULT_FILTERS;
    } catch { return DEFAULT_FILTERS; }
  });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [searchActive, setSearchActive] = useState(false);
  const [togglingSearch, setTogglingSearch] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [togglingEmail, setTogglingEmail] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [togglingSms, setTogglingSms] = useState(false);

  // Construit la query string des filtres pour la recherche serveur
  const buildQuery = (pg) => {
    const p = new URLSearchParams({ view: "transporter", page: String(pg), page_size: "20" });
    if (filters.keyword.trim()) p.set("q", filters.keyword.trim());
    if (filters.city.trim()) p.set("city", filters.city.trim());
    if (filters.vehicleType) p.set("vehicle_type", filters.vehicleType);
    if (filters.priceMin) p.set("price_min", filters.priceMin);
    if (filters.priceMax) p.set("price_max", filters.priceMax);
    if (filters.maxDistance) p.set("max_distance", filters.maxDistance);
    if (filters.minShipperRating) p.set("min_shipper_rating", filters.minShipperRating);
    if (filters.sort) p.set("sort", filters.sort);
    return p.toString();
  };

  // Recherche serveur des missions (filtres appliqués côté base)
  const fetchMissions = async (pg = 1) => {
    setSearching(true);
    try {
      const { data } = await api.get(`/missions?${buildQuery(pg)}`);
      setMissionsData(data || { missions: [], my_missions: [] });
      setSearchActive(Boolean(data?.search_active));
      setEmailEnabled(data?.nearby_email_enabled !== false);
      setSmsEnabled(Boolean(data?.sms_alerts_enabled));
      if (data?.pagination) setPagination(data.pagination);
      setPage(pg);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const loadAll = async () => {
    try {
      const [p, e, u] = await Promise.all([
        api.get("/transporter/profile").catch(() => ({ data: null })),
        api.get("/transporter/earnings").catch(() => ({ data: null })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setProfile(p.data);
      if (e.data) setEarnings(e.data);
      if (u.data) setUser(u.data);
      await fetchMissions(1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Chargement initial ; les filtres sont traités par l'effet debouncé suivant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  // Recherche serveur debouncée à chaque changement de filtre (400 ms)
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => { fetchMissions(1); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.keyword, filters.city, filters.vehicleType, filters.priceMin, filters.priceMax, filters.maxDistance, filters.minShipperRating, filters.sort]);

  // Mémorise les filtres pour la prochaine visite (persistance locale)
  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* quota/private mode */ }
  }, [filters]);

  const apply = async (id) => {
    try {
      const { data } = await api.post(`/missions/${id}/apply`);
      const mins = data?.validity_minutes || 60;
      toast.success(`Candidature envoyée — valable ${mins} min. L'expéditeur choisira parmi les candidats.`);
      fetchMissions(page);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const withdraw = async (id) => {
    try {
      await api.post(`/missions/${id}/withdraw`);
      toast.success("Candidature retirée");
      fetchMissions(page);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtCountdown = (secs) => {
    if (secs == null || secs <= 0) return "expirée";
    const m = Math.floor(secs / 60), s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const toggleSearchActive = async () => {
    if (!profile) {
      toast.error("Configurez d'abord votre profil transporteur");
      return;
    }
    setTogglingSearch(true);
    try {
      const next = !searchActive;
      await api.patch("/transporter/search-active", { active: next });
      setSearchActive(next);
      toast.success(next ? "🔔 Mode recherche activé — vous serez notifié des missions proches" : "Mode recherche désactivé");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTogglingSearch(false); }
  };

  const toggleEmailEnabled = async () => {
    setTogglingEmail(true);
    try {
      const next = !emailEnabled;
      await api.patch("/transporter/nearby-email", { enabled: next });
      setEmailEnabled(next);
      toast.success(next ? "✉️ Emails « mission proche » activés" : "Emails « mission proche » désactivés");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTogglingEmail(false); }
  };

  const toggleSmsAlerts = async () => {
    setTogglingSms(true);
    try {
      const next = !smsEnabled;
      const { data } = await api.patch("/transporter/sms-alerts", { enabled: next });
      setSmsEnabled(next);
      if (next && data?.phone_valid === false) {
        toast.warning("Alertes SMS activées — ajoutez un numéro de téléphone valide dans votre compte pour les recevoir.");
      } else {
        toast.success(next ? "📱 Alertes SMS activées" : "Alertes SMS désactivées");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTogglingSms(false); }
  };

  const kycStatus = missionsData.kyc_status;
  const kycColor = kycStatus === "validated" ? "bg-green-600" : kycStatus === "pending" ? "bg-amber-500" : "bg-primary";

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="transporter-dashboard">
      <BackButton to="/" label="Retour à l'accueil" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-accent mb-2">— Transporteur</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Missions disponibles</h1>
        </div>
        <Link to="/kyc" data-testid="btn-kyc">
          <Button variant="outline" className="rounded-md border-[#1F4032] h-11 gap-2">
            <ShieldCheck className="w-4 h-4" /> Mes documents
          </Button>
        </Link>
      </div>

      {/* KYC Banner */}
      <div className={`p-5 mb-8 flex flex-wrap items-center justify-between gap-4 text-white ${kycColor}`} data-testid="kyc-banner">
        <div className="flex items-center gap-3">
          {kycStatus === "validated"
            ? <ShieldCheck className="w-6 h-6" />
            : <ShieldAlert className="w-6 h-6" />}
          <div>
            <div className="overline text-white/80">Statut KYC</div>
            <div className="font-display font-bold text-lg">
              {kycStatus === "validated" ? "Validé — vous pouvez accepter des missions"
                : kycStatus === "pending" ? "Vos documents sont en cours de vérification"
                : "Documents incomplets ou refusés"}
            </div>
            {kycStatus === "pending" && (
              <div className="text-white/90 text-xs mt-1">
                Vérification IA + équipe sous quelques minutes en moyenne. Vous serez notifié par email dès l'approbation.
                Vous pouvez déjà consulter les missions disponibles ci-dessous.
              </div>
            )}
          </div>
        </div>
        {kycStatus !== "validated" && (
          <Link to="/kyc">
            <Button className="rounded-md bg-white text-black hover:bg-white/90">
              Compléter le KYC <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>

      {/* Profile card */}
      <TransporterProfileCard profile={profile} onSaved={loadAll} />
      <DriverRegistry />

      {/* Toggle Mode recherche */}
      {profile && (
        <div className={`mt-6 border rounded-md p-5 flex flex-wrap items-center justify-between gap-4 transition-colors card-tactile ${searchActive ? "border-emerald-500 bg-emerald-50 pulse-glow" : "border-[#1F4032]/10 bg-white"}`} data-testid="search-mode-card">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            {searchActive
              ? <BellRing className="w-5 h-5 text-emerald-600 mt-0.5 animate-pulse" />
              : <BellOff className="w-5 h-5 text-neutral-400 mt-0.5" />}
            <div>
              <div className="font-display font-bold text-lg tracking-tight">
                {searchActive ? "Mode recherche actif" : "Mode recherche désactivé"}
              </div>
              <div className="text-sm text-neutral-600 mt-1 max-w-md">
                {searchActive
                  ? `Vous serez notifié dès qu'une mission compatible est postée dans votre zone (rayon ${profile.radius_km} km autour de ${profile.base_city}).`
                  : "Activez pour recevoir une notification instantanée pour chaque nouvelle mission compatible postée près de vous."}
              </div>
            </div>
          </div>
          <Button
            onClick={toggleSearchActive}
            disabled={togglingSearch}
            className={`rounded-md gap-2 ${searchActive ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#1F4032] hover:bg-[#2A5443] text-white"}`}
            data-testid="toggle-search-active-btn"
          >
            {searchActive ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
            {togglingSearch ? "..." : (searchActive ? "Désactiver" : "Activer la recherche")}
          </Button>
        </div>
      )}

      {/* Sous-toggle : email "mission proche" (visible seulement si mode recherche actif) */}
      {profile && searchActive && (
        <div className="mt-3 border border-emerald-500/30 bg-emerald-50/60 rounded-md p-4 flex flex-wrap items-center justify-between gap-3" data-testid="email-toggle-card">
          <div className="flex items-start gap-3 flex-1 min-w-[240px]">
            {emailEnabled
              ? <Mail className="w-5 h-5 text-emerald-700 mt-0.5" />
              : <MailX className="w-5 h-5 text-neutral-400 mt-0.5" />}
            <div>
              <div className="font-display font-bold text-base tracking-tight">
                Alertes email « mission proche »
              </div>
              <div className="text-xs text-neutral-600 mt-1 max-w-md">
                {emailEnabled
                  ? "Vous recevez aussi un email à chaque nouvelle mission compatible (1 max par heure pour éviter le spam)."
                  : "Désactivé — vous ne recevez que les notifications dans l'app."}
              </div>
            </div>
          </div>
          <Button
            onClick={toggleEmailEnabled}
            disabled={togglingEmail}
            variant="outline"
            size="sm"
            className={`rounded-md gap-2 ${emailEnabled ? "border-emerald-600 text-emerald-700 hover:bg-emerald-100" : "border-neutral-400 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid="toggle-nearby-email-btn"
          >
            {emailEnabled ? <MailX className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
            {togglingEmail ? "..." : (emailEnabled ? "Désactiver" : "Activer")}
          </Button>
        </div>
      )}

      {/* Notifications push (Web Push + GPS temps réel + grille gabarit) */}
      {profile && <PushNotificationsCard vehicleType={profile.vehicle_type} />}

      {/* Alertes SMS (opt-in) — pour les transporteurs qui ne consultent pas l'app */}
      {profile && (
        <div className={`mt-3 border rounded-md p-4 flex flex-wrap items-center justify-between gap-3 ${smsEnabled ? "border-emerald-500/40 bg-emerald-50/60" : "border-[#1F4032]/10 bg-white"}`} data-testid="sms-toggle-card">
          <div className="flex items-start gap-3 flex-1 min-w-[240px]">
            {smsEnabled
              ? <Smartphone className="w-5 h-5 text-emerald-700 mt-0.5" />
              : <MessageSquareOff className="w-5 h-5 text-neutral-400 mt-0.5" />}
            <div>
              <div className="font-display font-bold text-base tracking-tight">Alertes SMS « mission proche »</div>
              <div className="text-xs text-neutral-600 mt-1 max-w-md">
                {smsEnabled
                  ? "Vous recevez un SMS quand une mission compatible est postée près de vous. Nécessite un numéro de téléphone valide sur votre compte."
                  : "Activez pour recevoir un SMS à chaque nouvelle mission compatible — idéal si vous ne consultez pas l'app régulièrement."}
              </div>
            </div>
          </div>
          <Button
            onClick={toggleSmsAlerts}
            disabled={togglingSms}
            variant="outline"
            size="sm"
            className={`rounded-md gap-2 ${smsEnabled ? "border-emerald-600 text-emerald-700 hover:bg-emerald-100" : "border-neutral-400 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid="toggle-sms-alerts-btn"
          >
            {smsEnabled ? <MessageSquareOff className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
            {togglingSms ? "..." : (smsEnabled ? "Désactiver" : "Activer")}
          </Button>
        </div>
      )}

      {/* Stripe Connect activation */}
      <StripeConnectCard />

      {/* Billing info (SIRET, adresse facturation pour PDFs) */}
      <BillingInfoCard user={user} onSaved={setUser} />

      {/* Parrainage — gagnez des crédits 0 % commission */}
      {/* Parrainage retiré du lancement : la commission affichée et contractée
          reste uniforme à 15 %, sans promotion implicite. */}

      {/* Analytics graphique (revenus, missions, notes) */}
      <TransporterAnalytics />

      {/* Gains (escrow) */}
      <div className="mt-8 grid md:grid-cols-3 gap-3" data-testid="earnings-block">
        <div className="p-5 rounded-md bg-neutral-950 text-white">
          <Wallet className="w-4 h-4 text-white/70 mb-2" />
          <div className="overline text-white/60">Gains totaux</div>
          <div className="font-display font-black text-3xl mt-1">{Number(earnings.total || 0).toFixed(2)}€</div>
        </div>
        <div className="p-5 rounded-md border border-neutral-200">
          <CreditCard className="w-4 h-4 text-neutral-600 mb-2" />
          <div className="overline">En attente de reversement</div>
          <div className="font-display font-black text-3xl mt-1">{Number(earnings.pending_payout || 0).toFixed(2)}€</div>
          <div className="text-xs text-neutral-500 mt-1">Reversement déclenché après preuve et confirmation, via Stripe Connect</div>
        </div>
        <div className="p-5 rounded-md border border-neutral-200">
          <div className="overline">Missions livrées</div>
          <div className="font-display font-black text-3xl mt-1">{earnings.missions_count || 0}</div>
        </div>
      </div>

      {/* Mes missions en cours */}
      {(missionsData.my_missions || []).length > 0 && (
        <div className="mt-10" data-testid="my-missions">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Mes missions</h2>
          <div className="border border-neutral-200 rounded-md">
            {missionsData.my_missions.map((m) => (
              <Link key={m.id} to={`/missions/${m.id}`}
                className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-neutral-200 last:border-0 hover:bg-muted/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-neutral-500 mb-1">
                    {m.pickup_address} → {m.delivery_address}
                  </div>
                  <div className="font-display font-bold">{m.title}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-display font-bold">{m.transporter_amount}€</div>
                  {m.payment_status && PAYMENT_LABEL[m.payment_status] && (
                    <span className={`px-2 py-1 text-[10px] font-mono rounded-md ${PAYMENT_LABEL[m.payment_status].color}`}>
                      💳 {PAYMENT_LABEL[m.payment_status].label}
                    </span>
                  )}
                  <span className="px-2 py-1 text-[10px] font-mono rounded-md bg-neutral-100">{m.status?.toUpperCase()}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Missions list */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Missions compatibles</h2>

        {/* Filtres (recherche serveur) */}
        {profile && (
          <>
            <div className="mb-3 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                value={filters.keyword}
                onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                placeholder="Rechercher par titre, ville, description…"
                className="w-full h-11 pl-10 pr-10 rounded-md border border-[#1F4032]/20 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4032]/30"
                data-testid="missions-keyword-search"
              />
              {searching && <Loader2 className="absolute right-9 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F4032] animate-spin" />}
              {filters.keyword && (
                <button
                  onClick={() => setFilters({ ...filters, keyword: "" })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500 hover:text-[#1F4032] font-mono"
                  data-testid="missions-keyword-clear"
                  aria-label="Effacer la recherche"
                >
                  ✕
                </button>
              )}
            </div>
          <div className="flex flex-wrap items-end gap-3 mb-4 p-4 border border-[#1F4032]/10 bg-white rounded-md" data-testid="filters-bar">
            <div>
              <div className="overline mb-1">Ville (départ/arrivée)</div>
              <input type="text" value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                className="w-40 h-9 border border-input rounded-md px-2 text-sm" placeholder="Ex : Lyon"
                data-testid="filter-city" />
            </div>
            <div>
              <div className="overline mb-1">Type de véhicule</div>
              <select value={filters.vehicleType}
                onChange={(e) => setFilters({ ...filters, vehicleType: e.target.value })}
                className="h-9 border border-input rounded-md px-2 text-sm bg-background"
                data-testid="filter-vehicle-type">
                <option value="">Tous</option>
                <option value="car">{VEHICLE_LABEL.car || "Voiture"}</option>
                <option value="van">{VEHICLE_LABEL.van || "Utilitaire"}</option>
                <option value="truck">{VEHICLE_LABEL.truck || "Camion"}</option>
                <option value="heavy_truck">{VEHICLE_LABEL.heavy_truck || "Poids lourd"}</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1">Prix min (€)</div>
              <input type="number" min="0" value={filters.priceMin}
                onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
                className="w-24 h-9 border border-input rounded-md px-2 text-sm" placeholder="0"
                data-testid="filter-price-min" />
            </div>
            <div>
              <div className="overline mb-1">Prix max (€)</div>
              <input type="number" min="0" value={filters.priceMax}
                onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
                className="w-24 h-9 border border-input rounded-md px-2 text-sm" placeholder="∞"
                data-testid="filter-price-max" />
            </div>
            <div>
              <div className="overline mb-1">Distance max de moi (km)</div>
              <input type="number" min="0" value={filters.maxDistance}
                onChange={(e) => setFilters({ ...filters, maxDistance: e.target.value })}
                className="w-28 h-9 border border-input rounded-md px-2 text-sm" placeholder="∞"
                data-testid="filter-max-distance" />
            </div>
            <div>
              <div className="overline mb-1 flex items-center gap-1"><Star className="w-3 h-3" /> Note expéditeur min</div>
              <select value={filters.minShipperRating}
                onChange={(e) => setFilters({ ...filters, minShipperRating: e.target.value })}
                className="h-9 border border-input rounded-md px-2 text-sm bg-background"
                data-testid="filter-min-shipper-rating">
                <option value="">Aucune</option>
                <option value="3">3★ et +</option>
                <option value="3.5">3,5★ et +</option>
                <option value="4">4★ et +</option>
                <option value="4.5">4,5★ et +</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1">Trier par</div>
              <select value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                className="h-9 border border-input rounded-md px-2 text-sm bg-background"
                data-testid="filter-sort">
                <option value="distance">Distance (proche)</option>
                <option value="price_desc">Prix (élevé → bas)</option>
                <option value="price_asc">Prix (bas → élevé)</option>
                <option value="rating_desc">Note expéditeur (haute → basse)</option>
                <option value="recent">Plus récent</option>
              </select>
            </div>
            {(filters.keyword || filters.city || filters.vehicleType || filters.priceMin || filters.priceMax || filters.maxDistance || filters.minShipperRating) && (
              <Button variant="ghost" size="sm"
                className="h-9 text-xs"
                onClick={() => setFilters({ keyword: "", city: "", vehicleType: "", priceMin: "", priceMax: "", maxDistance: "", minShipperRating: "", sort: filters.sort })}
                data-testid="filter-reset">Réinitialiser les filtres</Button>
            )}
          </div>
          </>
        )}

        {loading ? (
          <div className="font-mono text-sm text-muted-foreground">CHARGEMENT…</div>
        ) : !profile ? (
          <div className="border border-dashed border-[#1F4032]/20 p-10 text-center text-muted-foreground">
            Configurez d'abord votre profil transporteur ci-dessus.
          </div>
        ) : searching ? (
          <div className="border border-dashed border-[#1F4032]/20 p-10 text-center text-muted-foreground flex items-center justify-center gap-2" data-testid="missions-searching">
            <Loader2 className="w-4 h-4 animate-spin" /> Recherche en cours…
          </div>
        ) : (missionsData.missions || []).length === 0 ? (
          <div className="border border-dashed border-[#1F4032]/20 p-10 text-center text-muted-foreground" data-testid="missions-empty">
            {(filters.keyword || filters.city || filters.vehicleType || filters.priceMin || filters.priceMax || filters.maxDistance || filters.minShipperRating) ? (
              <>Aucune mission ne correspond à votre recherche. <button onClick={() => setFilters({ keyword: "", city: "", vehicleType: "", priceMin: "", priceMax: "", maxDistance: "", minShipperRating: "", sort: "distance" })} className="underline" data-testid="missions-empty-reset">Réinitialiser les filtres</button></>
            ) : (
              <>Aucune mission compatible dans votre zone. Augmentez votre rayon ou attendez de nouvelles missions.</>
            )}
          </div>
        ) : (
          <div className="border border-[#1F4032]/10">
            {(missionsData.missions || []).map((m) => {
              const r = m.shipper_rating || { average: 0, count: 0 };
              return (
              <div
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-4 p-5 border-b border-[#1F4032]/10 last:border-0 hover:bg-muted/50 transition-colors"
                data-testid={`mission-available-${m.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {m.pickup_address} → {m.delivery_address}
                  </div>
                  <div className="font-display font-bold text-lg flex items-center gap-2 flex-wrap">
                    {m.title}
                    {r.count > 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-xs font-mono px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid={`shipper-rating-${m.id}`}>
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                        {r.average.toFixed(1)} <span className="text-amber-600/70">({r.count})</span>
                      </span>
                    ) : (
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500" data-testid={`shipper-rating-${m.id}`}>
                        Nouvel expéditeur
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    À {m.distance_from_you_km} km · Trajet {m.distance_km} km · {m.cargo_weight_kg} kg · {(VEHICLE_LABEL[m.vehicle_type] || m.vehicle_type).toUpperCase()}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-display font-bold text-2xl">{m.price}€</div>
                  <Link to={`/missions/${m.id}`}>
                    <Button variant="outline" className="rounded-md border-[#1F4032]">Voir</Button>
                  </Link>
                  {m.my_candidature?.status === "active" ? (
                    <div className="flex items-center gap-2" data-testid={`candidature-status-${m.id}`}>
                      <span className="text-xs font-mono px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                        Candidaté · {fmtCountdown(m.my_candidature.seconds_left)}
                      </span>
                      <Button onClick={() => withdraw(m.id)} variant="outline"
                        className="rounded-md border-neutral-300 text-neutral-700"
                        data-testid={`withdraw-${m.id}`}>Retirer</Button>
                    </div>
                  ) : m.my_candidature?.status === "selected" ? (
                    <span className="text-xs font-mono px-2 py-1 rounded bg-[#1F4032] text-white" data-testid={`candidature-status-${m.id}`}>Choisi ✓</span>
                  ) : (
                    <Button onClick={() => apply(m.id)} disabled={missionsData.blocked}
                      className="rounded-md bg-[#1F4032] hover:bg-[#2A5443]"
                      data-testid={`apply-${m.id}`}>
                      Candidater
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Pagination serveur */}
        {profile && !searching && (missionsData.missions || []).length > 0 && pagination.total_pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3" data-testid="missions-pagination">
            <Button variant="outline" size="sm" className="rounded-md"
              disabled={page <= 1}
              onClick={() => fetchMissions(page - 1)}
              data-testid="pagination-prev">Précédent</Button>
            <span className="text-sm text-neutral-600 font-mono" data-testid="pagination-info">
              Page {pagination.page} / {pagination.total_pages} · {pagination.total} mission{pagination.total > 1 ? "s" : ""}
            </span>
            <Button variant="outline" size="sm" className="rounded-md"
              disabled={page >= pagination.total_pages}
              onClick={() => fetchMissions(page + 1)}
              data-testid="pagination-next">Suivant</Button>
          </div>
        )}
      </div>
    </div>
  );
}
